import Logger from '@utils/log'
import dayjs from 'dayjs'
import deepmerge from 'deepmerge'
import * as actions from '@actions'
import config from '@/config'
import * as store from '@store'

const log = new Logger('utils')

export const noop = () => {
    
    // Intentionally blank
    
}

export const randomNumber = (from, to) =>
    Math.floor(Math.random() * (to - from + 1) + from)

export const randomColor = () => Math.floor(Math.random() * 16777215).toString(16)

export const sleep = (delayMillis = 1000) => {
    return new Promise(resolve => setTimeout(resolve, delayMillis))
}

// searchDocument: SearchDocument
export const getQueryString = searchDocument => {
    let queryString = ''
    
    for (let key in searchDocument) {
        let value = (searchDocument)[key]
        
        if (value !== 'undefined' && value !== '')
            queryString += key + '=' + value + '&'
    }
    
    return queryString
}

/**
 * Gets the "full" grantor/ee for a given `doc` document,
 * where full represents the person that was parsed via the ML model
 *
 * @param doc Document associated with the grantor/ee
 * @param field Field to use for sorting (`grantor` or `grantee`)
 * @returns The "full" grantor/ee that was parsed via the ML model
 */
export const getFullGrantorGrantee = (doc, field) => {
    if (!doc) return ''
    
    try {
        const sortedValues = doc.values?.sort((a, b) => {
            if (dayjs(a).isAfter(dayjs(b))) return -1
            if (dayjs(a).isBefore(dayjs(b))) return 1
            return 0
        })
        
        return sortedValues && sortedValues.length ? sortedValues[0][field] : ''
    } catch (e) {
        log.w('getFullGrantorGrantee', e)
        return ''
    }
}


// @todo @debug
// Inverts all colors for easier daytime reading
export const invert = enabled => {
    
    const key = 'debug-inverted'
    
    // eslint-disable-next-line no-restricted-globals
    const html = document.querySelector('html')
    
    if (!enabled && html.classList.contains(key))
        html.classList.remove(key)
    else if (enabled && !html.classList.contains(key))
        html.classList.add(key)
    
}

export const _omit = (obj, keys) =>
    Object.keys(obj).reduce((acc, it) => {
        if (typeof keys === 'string' && it === keys)
            return acc
        else if (keys.includes(it))
            return acc
        else
            return { ...acc, [it]: obj[it] }
    }, {})

export const _includeOnly = (obj, keys) =>
    Object.keys(obj).reduce((acc, it) => {
        if (typeof keys === 'string' && it === keys)
            return { ...acc, [it]: obj[it] }
        else if (keys.includes(it))
            return { ...acc, [it]: obj[it] }
        else
            return acc
    }, {})

export const omit = (obj, keys, invert = false) =>
    invert ? _includeOnly(obj, keys) : _omit(obj, keys)

export const omitFalsey = obj =>
    Object.keys(obj).reduce((acc, it) => {
        const value = obj[it]
        
        if (value === undefined)
            return acc
        return { ...acc, [it]: obj[it] }
    }, {})

// https://youmightnotneed.com/lodash
export const has = (obj, path) => {
    
    // Regex explained: https://regexr.com/58j0k
    const pathArray = Array.isArray(path) ? path : path.match(/([^[.\]])+/g)
    
    return !!pathArray.reduce((prevObj, key) => prevObj && prevObj[key], obj)
    
}

// https://youmightnotneed.com/lodash
export const get = (obj, path, defValue) => {
    
    // If path is not defined or it has false value
    if (!path) return undefined
    
    // Check if path is string or array. Regex : ensure that we do not have '.' and brackets.
    // Regex explained: https://regexr.com/58j0k
    const pathArray = Array.isArray(path) ? path : path.match(/([^[.\]])+/g)
    
    // Find value
    const result = pathArray.reduce(
        (prevObj, key) => prevObj && prevObj[key],
        obj,
    )
    
    // If found value is undefined return default value; otherwise return the value
    return result === undefined ? defValue : result
    
}

// https://youmightnotneed.com/lodash
export const set = (obj, path, value) => {
    
    // Regex explained: https://regexr.com/58j0k
    const pathArray = Array.isArray(path) ? path : path.match(/([^[.\]])+/g)
    
    pathArray.reduce((acc, key, i) => {
        if (acc[key] === undefined) acc[key] = {}
        if (i === pathArray.length - 1) acc[key] = value
        return acc[key]
    }, obj)
    
}

export const snippet = (text, len = 3) => {
    
    if (text.length < (len * 2)) return text
    
    return text.substring(0, len) + '...' + text.substring(text.length - len)
    
}

export const zeroPad = (number, minLength = 2) => {
    
    const value = number.toString()
    const prefix = value.length >= minLength ? '' : '0'.repeat(minLength - value.length)
    
    return `${prefix}${value}`
    
}

export const formatJsonNewLines = obj => {
    
    let value
    
    try {
        value = JSON.stringify(JSON.parse(obj), null, 4)
    } catch (e) {
        value = JSON.stringify(obj, null, 4)
    }
    
    try {
        value = value.replace(/\\n/ig, '\n')
    } catch (e) {
        // NOOP
    }
    
    return value ?? ''
    
}

export const waitFor = (conditionFn, delayMillis = 1000, maxAttempts = null) => {
    
    return new Promise(resolve => {
        
        let t
        let attempts = 0
        
        t = setInterval(() => {
            
            attempts++
            
            if (maxAttempts && attempts >= maxAttempts) {
                clearInterval(t)
                resolve(false)
            }
            
            if (conditionFn()) {
                clearInterval(t)
                resolve(true)
            }
            
            
        }, delayMillis)
        
    })
    
}

export const copyToClipboard = async text => {
    
    // Clipboard API not available
    if (!navigator.clipboard)
        throw new Error('Your browser doesn\'t allow clipboard access')
    
    try {
        await navigator.clipboard.writeText(text)
    } catch (e) {
        log.error('Failed to copy', e)
        throw new Error('Could not copy to your clipboard')
    }
    
}

export const createQueryString = (...maps) => {
    
    const params = new URLSearchParams()
    
    const appendValue = (key, value) => {
        if (Array.isArray(value)) {
            value.forEach(v => {
                if (v)
                    params.append(key, v)
            })
        } else if (value) {
            params.append(key, value)
        }
    }
    
    maps.forEach(map => {
        
        if (Array.isArray(map))
            map.forEach(([key, value]) => appendValue(key, value))
        else
            Object.keys(map).forEach(it => appendValue(it, map[it]))
        
    })
    
    return params.toString()?.length ? `?${params.toString()}` : ''
    
}

/**
 * Debounces a function
 * Note: explicitly using `function` here to maintain context
 * 
 * @param {Function} fn Function to run
 * @param {Number} delayMillis Time to wait between executions
 * @returns {Function}
 */
export function debounce(fn, delayMillis = 500) {
    
    let timer = null
    
    return (...args) => {
        
        clearTimeout(timer)
        
        timer = setTimeout(() => fn.apply(this, args), delayMillis)
        
    }
    
}

/* export function debounce(fn, delayMillis = 500, cb) {
    
    let timer = null
    
    return (...args) => {
        
        clearTimeout(timer)
        
        timer = setTimeout(() => cb(fn.apply(this, args)), delayMillis)
        
    }
    
} */

/**
 * debounce(func, [wait=0], [options={}])
 *
 * @param {Function} func The function to debounce.
 * @param {number} [delayMillis=0] The number of milliseconds to delay.
 * @param {Object} [options={}] The options object.
 * @param {boolean} [options.leading=false] Specify invoking on the leading edge of the timeout.
 * @param {cancelObj} [options.cancelObj='canceled'] Specify the error object to be rejected.
 * @returns {Function} Returns the new debounced function.
 */
export function debounceAsync(
    func,
    delayMillis = 0,
    {
        leading = false,
        cancelObj = 'canceled',
        defaultValue = undefined,
    } = {},
) {
    
    let timerId, latestResolve, shouldCancel
    
    return function (...args) {
        
        if (!latestResolve) {
            // The first call since last invocation.
            return new Promise((resolve, _reject) => {
                
                latestResolve = resolve
                
                const reject = defaultValue === undefined ? _reject : () => resolve(defaultValue)
                
                if (leading) {
                    invokeAtLeading.apply(this, [args, resolve, reject])
                } else {
                    timerId = setTimeout(
                        invokeAtTrailing.bind(this, args, resolve, reject),
                        delayMillis,
                    )
                }
                
            })
        }
        
        shouldCancel = true
        
        return new Promise((resolve, _reject) => {
            
            latestResolve = resolve
            
            const reject = defaultValue === undefined ? _reject : () => resolve(defaultValue)
            
            timerId = setTimeout(
                invokeAtTrailing.bind(this, args, resolve, reject),
                delayMillis,
            )
            
        })
        
    }
    
    function invokeAtLeading(args, resolve, reject) {
        func.apply(this, args).then(resolve).catch(reject)
        shouldCancel = false
    }
    
    function invokeAtTrailing(args, resolve, reject) {
        if (shouldCancel && resolve !== latestResolve) {
            reject(cancelObj)
        } else {
            func.apply(this, args).then(resolve).catch(reject)
            shouldCancel = false
            clearTimeout(timerId)
            timerId = latestResolve = null
        }
    }
    
}

export const setAbortableTimeout = (callback, delayInMilliseconds, customAbortController) => {
    
    const controller = customAbortController || new AbortController()
    const signal = controller.signal
    
    const internalCallback = () => {
        signal.removeEventListener('abort', handleAbort)
        callback()
    }
    
    // Set up our internal timer that we can clear-on-abort.
    const t = setTimeout(internalCallback, delayInMilliseconds)
    
    const handleAbort = () => {
        // console.warn('Canceling timer (%s) via signal abort.', t)
        clearTimeout(t)
    }
    
    // When the calling context triggers an abort, we need to listen to for it so
    // that we can turn around and clear the internal timer.
    // --
    // NOTE: We're creating a proxy callback to remove this event-listener once
    // the timer executes. This way, our event-handler never gets invoked if
    // there's nothing for it to actually do. Also note that the 'abort' event
    // will only ever get emitted once, regardless of how many times the calling
    // context tries to invoke .abort() on its AbortController.
    signal.addEventListener('abort', handleAbort)
    
    return controller
    
}

/*
 * serial executes Promises sequentially.
 * @param {funcs} An array of funcs that return promises.
 * @example
 * const urls = ['/url1', '/url2', '/url3']
 * serial(urls.map(url => () => $.ajax(url)))
 *     .then(console.log.bind(console))
 */
export const promiseSerial = funcs =>
    funcs.reduce((promise, func) =>
        promise.then(result => func().then(Array.prototype.concat.bind(result))), Promise.resolve([]))

/**
 * Downloads a file from a URL and returns a Blob
 * 
 * @param {string} url - The URL of the file to download
 * @param {boolean} throwOnError - If true, throw an error if the file fails to download
 * @param {function} onComplete - Callback to run after the file is downloaded
 * @returns {Promise<DownloadFileBlobResult>}
 */
export const downloadFileBlob = async (url, throwOnError = true, onComplete = noop) => {
    
    try {
        
        const name = url.split('/').pop()
        const res = await fetch(url)
        
        if (!res.ok)
            throw new Error(`Failed to download file "${name}"`)
        
        const data = await res.blob()
        
        onComplete?.()
        
        return {
            name,
            data,
        }
        
    } catch (e) {
        
        console.error('Error fetching file:', e)
        
        onComplete?.()
        
        if (throwOnError)
            throw e
        else
            return null
        
    }
    
}

/**
 * Downloads multiple files from URLs and returns Blobs
 * 
 * @param {string[]} urls - An array of URLs to download
 * @param {boolean} throwOnError - If true, throw an error if any file fails to download
 * @param {function} onComplete - Callback to run after all files are downloaded
 * @returns {Promise<DownloadFileBlobResult[]>}
 */
export const downloadAllFilesBlobs = async (urls = [], throwOnError = true, onComplete = noop) => {
    
    return await Promise.all(urls.map(it => downloadFileBlob(it, throwOnError, onComplete)))
    
}

export const arrayToCsvBlobDefaultRowMapper = (key, value) => value

export const arrayToCsvBlob = (array, mimeType, includeHeader = true, rowMapperFn = arrayToCsvBlobDefaultRowMapper) => {
    
    let keys = []
    
    const rows = array.map(row => {
        
        if (Array.isArray(row))
            return `"${row.map(col => rowMapperFn(undefined, col)).join('","')}"`
        
        if (!keys.length)
            keys = Object.keys(row)
        
        return keys.map(key => `"${rowMapperFn(key, row[key])}"`).join(',')
        
    })
    
    // Add the header row if there are keys
    if (includeHeader && keys.length)
        rows.unshift(`"${keys.join('","')}"`)
    
    return new Blob([rows.join('\n')], { type: mimeType })
    
}

export const reconstituteBlobFromJson = (jsonBlob, mimeType) => {
    
    const uint8Array = new Uint8Array(jsonBlob)
    
    return new Blob([uint8Array], { type: mimeType })
    
}

/**
 * Creates a synthetic download of a file
 * 
 * @see https://www.npmjs.com/package/file-saver#supported-browsers
 * Related but overkill for our use case
 * 
 * @param {Blob} data - The data to download
 * @param {string} type - The MIME type of the data
 * @param {string} fileName - The name of the file to download
 * @returns {void}
 */
export const createSyntheticDownload = (data, type, fileName) => {
    
    let url
    
    if (typeof data === 'string' && data.startsWith('data:')) {
        // If the data is a base64 string, use it directly
        url = data
    } else {
        // Automatically wrap data as a blob, if it's not already
        const blob = data instanceof Blob ? data : new Blob([data], { type })
        
        url = window.URL.createObjectURL(blob)
    }
    
    // eslint-disable-next-line no-restricted-globals
    const link = document.createElement('a')
    
    link.href = url
    link.setAttribute('download', fileName)
    
    // eslint-disable-next-line no-restricted-globals
    document.body.appendChild(link)
    link.click()
    
    link.parentNode.removeChild(link)
    
}

export const downloadDocumentById = async documentId => {
    
    const res = await actions.downloadDocumentPdf(documentId)
    
    createSyntheticDownload(
        res.data,
        res.headers['content-type'],
        res.headers['content-name'],
    )
    
}

/**
 * Deduplicates an array
 * 
 * @param {Array} arr Source array
 * @param {String} [key] Optional key (default: 'id')
 * @returns 
 */
export const dedupe = (arr, key = 'id') => Object.values(
    arr.reduce((acc, it) => ({
        ...acc,
        [it[key]]: it,
    }), {}),
)

/**
 * Deduplicates an array of primitive values
 * 
 * @param {Array} arr Source array
 * @returns 
 */
export const dedupePrimitive = arr => Array.from(new Set(arr))

export const getAspectRatio = (width, height, maxWidth, maxHeight) => {
    
    if (width > maxWidth && width > height)
        return {
            ratio: width / height,
            orientation: 'horizontal',
        }
    
    if (height > maxHeight && height > width)
        return {
            ratio: height / width,
            orientation: 'vertical',
        }
    
    return {
        ratio: 0,
        orientation: null,
    }
    
}

export const scaleAspectRatio = (width, height, maxWidth, maxHeight) => {
    
    const { ratio, orientation } = getAspectRatio(width, height, maxWidth, maxHeight)
    
    if (orientation === 'horizontal') {
        
        return {
            ratio,
            orientation,
            width: maxWidth,
            height: maxWidth / ratio,
        }
        
    } else if (orientation === 'vertical') {
        
        return {
            ratio,
            orientation,
            width: maxHeight / ratio,
            height: maxHeight,
        }
        
    }
    
    return {
        ratio,
        orientation,
        width: maxWidth,
        height: maxHeight,
    }
    
}

export const scaleByAspectRatio = (width, height, ratio) => {
    
    if (width > height) return {
        width,
        height: width / ratio,
    }
    
    if (height > width) return {
        width: height / ratio,
        height,
    }
    
    return {
        width,
        height,
    }
    
}

export const titleCase = text => {
    
    try {
        
        const first = text?.substring(0, 1)?.toUpperCase() ?? ''
        const rest = text?.substring(1) || ''
        
        return first + rest
        
    } catch (e) {
        
        return text
        
    }
    
}

export const formatCurrency = (value, locale = 'en-US', currency = 'USD') => {
    
    try {
        
        // @todo why does eslint think this is undefined?
        // eslint-disable-next-line no-undef
        const formatter = new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
            
            // These options will round to whole numbers
            // minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
            // maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
        })
        
        return formatter.format(value)
        
    } catch (e) {
        
        console.error('formatCurrency', e)
        return '0'
        
    }
    
}

export const formatNumber = (value, locale = 'en-US') => {
    
    try {
        
        // @todo why does eslint think this is undefined?
        // eslint-disable-next-line no-undef
        const formatter = new Intl.NumberFormat(locale)
        
        return formatter.format(value)
        
    } catch (e) {
        
        console.error('formatNumber', e)
        return '0'
        
    }
    
}

/* export const arrayGroupBy = (arr, key, altsMap = {}) => {
    
    return arr.reduce((acc, obj) => {
        
        if (!Object.prototype.hasOwnProperty.call(obj, key))
            throw new Error(`Invalid or missing unique key "${key}"`)
        
        let hasKeys = true
        
        if (Object.keys(altsMap).length)
            hasKeys = Object.keys(obj)
                .filter(it => obj[it] === altsMap[it])
                .length === Object.keys(altsMap).length
        
        if (hasKeys)
            if (acc[obj[key]])
                acc[obj[key]].push(obj)
            else
                acc[obj[key]] = [obj]
        
        return acc
        
    }, {})
    
} */

export const arrayGroupByFn = (arr, keysFn, delim = ';') => {
    
    return arr.reduce((acc, obj) => {
        
        const keys = keysFn(obj)
        
        keys.forEach(key => {
            if (!Object.prototype.hasOwnProperty.call(obj, key))
                throw new Error(`Invalid or missing unique key "${key}"`)
        })
        
        const key = keys.reduce((acc, it) => [
            ...acc,
            obj[it],
        ], []).join(delim)
        
        if (acc[key])
            acc[key].push(obj)
        else
            acc[key] = [obj]
        
        return acc
        
    }, {})
    
}

export const arrayGroupBy = (arr, keys = [], delim = ';') => {
    return arrayGroupByFn(arr, () => Array.isArray(keys) ? keys : [keys], delim)
}

export const arrayGroupByPrefix = (arr, delim = '_') => {
    
    return arr.reduce((acc, it) => {
        
        if (!it.includes(delim)) {
            console.warn(`Delimiter "${delim}" not found in "${it}"`)
            return { ...acc, [it]: [it] }
        }
        
        const key = it.substring(0, it.indexOf(delim))
        
        if (!acc[key])
            acc[key] = []
        
        acc[key].push(it)
        
        return acc
        
    }, {})
    
}

const naturalCompare = (a = '', b = '') => {
    const ax = []
    const bx = []
    
    a.toString().replace(/(\d+)|(\D+)/g, (_, $1, $2) => {
        ax.push([$1 || Infinity, $2 || ''])
    })
    b.toString().replace(/(\d+)|(\D+)/g, (_, $1, $2) => {
        bx.push([$1 || Infinity, $2 || ''])
    })
    
    while (ax.length && bx.length) {
        const an = ax.shift()
        const bn = bx.shift()
        const nn = (an[0] - bn[0]) || an[1].localeCompare(bn[1])
        
        if (nn) return nn
    }
    
    return ax.length - bx.length
}

export const sortBy = (arr, key, mode = 'simple', ascending = true) => {
    const gt = ascending ? 1 : -1
    const lt = ascending ? -1 : 1
    
    return arr.sort((a, b) => {
        
        const aValue = get(a, key) || ''
        const bValue = get(b, key) || ''
        
        if (mode === 'natural') {
            return naturalCompare(aValue, bValue) * gt
        } else if (mode === 'date') {
            const dateA = aValue ? dayjs(aValue).unix() : 0
            const dateB = bValue ? dayjs(bValue).unix() : 0
            
            if (dateA > dateB) return gt
            if (dateA < dateB) return lt
            return 0
        } else {
            if (aValue > bValue) return gt
            if (aValue < bValue) return lt
            return 0
        }
    })
}

export const sortByMultiple = (arr, keys, mode = 'simple', ascending = true) => {
    
    if (!Array.isArray(keys) || keys.length === 1)
        return sortBy(arr, Array.isArray(keys) ? keys[0] : keys, mode, ascending)
    
    const gt = ascending ? 1 : -1
    const lt = ascending ? -1 : 1
    
    return arr.sort((a, b) => {
        const aValues = keys.map(key => get(a, key) || '')
        const bValues = keys.map(key => get(b, key) || '')
        
        let comparison = 0
        
        for (let i = 0; i < keys.length; i++) {
            const aValue = aValues[i]
            const bValue = bValues[i]
            
            if (mode === 'natural') {
                comparison = naturalCompare(aValue, bValue) * gt
            } else if (mode === 'date') {
                const dateA = aValue ? dayjs(aValue).unix() : 0
                const dateB = bValue ? dayjs(bValue).unix() : 0
                
                
                comparison = (dateA - dateB) * gt
            } else {
                if (aValue > bValue) return gt
                if (aValue < bValue) return lt
                comparison = 0
            }
            
            if (comparison) break
        }
        
        return comparison
    })
}

/**
 * Gets the full name of a user, or an email if no name is available
 * 
 * @param {User} user - The user object
 * @param {Boolean} useEmailIfNull - If true, returns the user's email if no name is available
 * @returns {String} The full name of the user
 */
export const getUserFullName = (user, useEmailIfNull = false) => {
    
    const first = user?.firstName?.trim() ?? ''
    const last = user?.lastName?.trim() ?? ''
    
    if (!first?.length && !last?.length)
        return useEmailIfNull
            ? (user?.email ?? '')
            : ''
    
    return `${first} ${last}`.trim()
    
}

export const getUserInitials = (user, useEmailIfNull = false) => {
    
    let text = getUserFullName(user, useEmailIfNull)
    
    if (!text?.length && !useEmailIfNull)
        return null
    
    if (!text?.length)
        text = (user?.email ?? 'X')
    
    text = text.trim()
    
    if (!text.includes(' '))
        text += ' X'
    
    const parts = text.split(' ')
    
    return `${parts[0].substring(0, 1)}${parts[1].substring(0, 1)}`.toUpperCase()
    
}

export const jsonClone = obj => JSON.parse(JSON.stringify(obj))

/**
 * Deep merges an object with child arrays,
 * de-duping the arrays if they have objects with IDs
 * 
 * @param {*} x source
 * @param {*} y target
 * @param {*} options options
 * @returns {*}
 */
export const deepmergeWithArrays = (x, y, options = {}) => {
    
    const arrayMerge = (destinationArray, sourceArray/* , options */) => {
        
        const destArray = [...(destinationArray || [])]   
        
        if (!destArray?.[0]?.id || !sourceArray?.[0]?.id) 
            return deepmerge(destArray, sourceArray, { clone: false })
        
        sourceArray?.forEach(item => {
            if (!destArray.some(it => it.id === item.id))
                destArray.push(item)
        })
        
        return destArray
        
    }
    
    return deepmerge(x, y, {
        arrayMerge,
        ...options,
    })
    
}

/**
 * Asynchronously loads an image from a given URL.
 *
 * @async
 * @function loadImageAsync
 * @param {string} url - The URL of the image to load.
 * @param {Object} [headers={}] - An optional object containing any headers to include with the request.
 * Defaults to an empty object.
 * @param {boolean} [anonymousCors=true] - An optional boolean indicating whether to set the crossOrigin property
 * of the image to 'Anonymous'. Defaults to true.
 * @throws {Error} Will throw an error if the image fails to load.
 * @returns {Promise<{blob: Blob, dataUrl: string}>} Returns a promise that resolves to an
 * object containing the loaded image's blob and data URL.
 */
export const loadImageAsync = async (url, headers = {}, anonymousCors = true) => {
    
    const img = new Image()
    
    if (anonymousCors)
        img.crossOrigin = 'Anonymous'
    
    const opts = {
        headers,
    }
    
    const res = await fetch(url, opts)
    
    if (!res.ok)
        throw new Error('Failed to load image')
    
    const blob = await res.blob()
    
    return {
        blob,
        dataUrl: URL.createObjectURL(blob),
    }
    
}


/**
 * @typedef LoadImageWithAuthorizationReturnValue
 * @property {String} dataUrl
 */
/**
 * Loads an image asynchronously using authorization headers.
 *
 * @async
 * @function loadImageWithAuthorization
 * @param {string} url - The URL of the image to load.
 * @throws {Error} Will throw an error if no valid token is found in local storage or if the URL starts with 'blob:'.
 * @returns {Promise<LoadImageWithAuthorizationReturnValue>} Returns a promise that resolves to an object
 * containing the loaded image's data URL.
 */
export const loadImageWithAuthorization = async url => {
    
    const token = localStorage.getItem('jwt')
    
    if (!token?.length)
        throw new Error('loadImageWithAuthorization: Invalid token')
    
    if (url?.startsWith('blob:'))
        return { dataUrl: url }
    
    return await loadImageAsync(url, {
        // @todo fix jwt not being present in store.user
        'x-api-key': token,
        'X-Organization-Id': store.currentOrganization.getValue()?.id,
    })
    
}

/*!
 * Check if two objects or arrays are equal
 * (c) 2021 Chris Ferdinandi, MIT License, https://gomakethings.com
 * @param  {*}       obj1 The first item
 * @param  {*}       obj2 The second item
 * @return {Boolean}       Returns true if they're equal in value
 */
export const isEqual = (obj1, obj2) => {
    
    /**
     * More accurately check the type of a JavaScript object
     * @param  {Object} obj The object
     * @return {String}     The object type
     */
    function getType(obj) {
        return Object.prototype.toString.call(obj).slice(8, -1).toLowerCase()
    }
    
    function areArraysEqual() {
        
        // Check length
        if (obj1.length !== obj2.length) return false
        
        // Check each item in the array
        for (let i = 0; i < obj1.length; i++) {
            if (!isEqual(obj1[i], obj2[i])) return false
        }
        
        // If no errors, return true
        return true
        
    }
    
    function areObjectsEqual() {
        
        if (Object.keys(obj1).length !== Object.keys(obj2).length) return false
        
        // Check each item in the object
        for (let key in obj1) {
            if (Object.prototype.hasOwnProperty.call(obj1, key)) {
                if (!isEqual(obj1[key], obj2[key])) return false
            }
        }
        
        // If no errors, return true
        return true
        
    }
    
    function areFunctionsEqual() {
        return obj1.toString() === obj2.toString()
    }
    
    function arePrimitivesEqual() {
        // Handle NaN comparison
        if (typeof obj1 === 'number' && typeof obj2 === 'number' && isNaN(obj1) && isNaN(obj2)) {
            return true
        }
        
        return obj1 === obj2
    }
    
    // Get the object type
    let type = getType(obj1)
    
    // If the two items are not the same type, return false
    if (type !== getType(obj2)) return false
    
    // Compare based on type
    if (type === 'array') return areArraysEqual()
    if (type === 'object') return areObjectsEqual()
    if (type === 'function') return areFunctionsEqual()
    
    return arePrimitivesEqual()
}

export const getFileSizeFormatted = fileSize => {
    const sizeSuffix = ['KB', 'MB', 'GB']
    let suffix = 'B'
    let result = fileSize
    
    for (const s of sizeSuffix) {
        if (result > 1024) {
            result = result / 1024
            suffix = s
        } else 
            break
    }
    
    return `~${result.toFixed(1)} ${suffix}`
}

export const looksLikeUuid = str => {
    
    const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/
    
    return uuidRegex.test(str)
    
}

/**
 * Builds a URL to fetch a binary file from GCS,
 * choosing the correct bucket based on the filename
 * 
 * @param {Object} record
 * @param {String} record.fileName
 * @param {Boolean} [onlyPdfs] Return null if the file isn't a PDF @todo @legacy
 * @param {Boolean} [isParse] Define whether its a parse collection or not
 * @returns {string|null}
 */
export const buildRecordUrl = (record, onlyPdfs = false, isParse = false) => {
    
    if (!record?.fileName?.length) return null
    
    const fileNameLc = record.fileName.toLowerCase()
    const isPdf = fileNameLc.endsWith('.pdf')
    const isPng = fileNameLc.endsWith('.png')
    
    // @legacy
    if (onlyPdfs && !isPdf)
        return null
    
    const endpoint = isParse
        ? 'indexfiles'
        : (isPdf ? 'files' : isPng && 'annotations')
    
    if (!endpoint?.length) return null
    
    return `${config.parseApiBaseUrl}/api/v1/pdfs/${endpoint}/${record.fileName}`
    
}

/**
 * Format bytes as human-readable text.
 * 
 * @param bytes Number of bytes.
 * @param si True to use metric (SI) units, aka powers of 1000. False to use 
 *           binary (IEC), aka powers of 1024.
 * @param dp Number of decimal places to display.
 * 
 * @return Formatted string.
 */
export const humanFileSize = (bytes, si=false, dp=1) => {
    const thresh = si ? 1000 : 1024
    
    if (Math.abs(bytes) < thresh) {
        return bytes + ' B'
    }
    
    const units = si 
        ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'] 
        : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB']
    let u = -1
    const r = Math.pow(10, dp)
    
    do {
        bytes /= thresh
        ++u
    } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1)
    
    
    return bytes.toFixed(dp) + ' ' + units[u]
}

/**
 * getOS - Detects the operating system of the user's device.
 * 
 * This function attempts to determine the operating system using `navigator.userAgentData`
 * if available (supported in newer browsers). If `userAgentData` is not available, it falls
 * back to parsing the `navigator.userAgent` string to identify the OS.
 * 
 * @returns {string} - A string representing the detected operating system. Possible values are:
 *   - 'MacOS'
 *   - 'iOS'
 *   - 'Windows'
 *   - 'Android'
 *   - 'Linux'
 *   - 'unknown' (if the OS cannot be determined)
 * 
 */
export const getOS = () => {
    const { userAgent, userAgentData } = navigator
    
    if (userAgentData) {
        // Use userAgentData if available (newer browsers)
        const platform = navigator.userAgentData.platform || ''
        
        if (platform.includes('macOS')) return 'MacOS'
        if (platform.includes('iOS')) return 'iOS'
        if (platform.includes('Windows')) return 'Windows'
        if (platform.includes('Android')) return 'Android'
        if (platform.includes('Linux')) return 'Linux'
    }
    
    if (/Macintosh|MacIntel|MacPPC|Mac68K/.test(userAgent)) return 'MacOS'
    if (/iPhone|iPad|iPod/.test(userAgent)) return 'iOS'
    if (/Win32|Win64|Windows|WinCE/.test(userAgent)) return 'Windows'
    if (/Android/.test(userAgent)) return 'Android'
    if (/Linux/.test(userAgent)) return 'Linux'
    
    return 'unknown'
}

export const getFormValuesObject = submitEvent => {
    
    const values = {}
    
    Array.from(submitEvent.target.elements).forEach(it => {
        if (it.name)
            values[it.name] = it.value
    })
    
    return values
    
}

export const obj2arr = obj => Object.keys(obj)
    .map(key => ({ [key]: obj[key] }))

export const isLikelyAGuid = str => {
    
    if (typeof str !== 'string')
        return false
    
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    
    return guidRegex.test(str)
    
}

export const isLikelyADate = str => {
    
    if (typeof str !== 'string')
        return false
    
    // Attempt to parse the string with Day.js
    const date = dayjs(str)
    
    // Check if the parsed date is valid
    return date.isValid()
    
}

/**
 * Simulates an upload process with random progress updates
 * 
 * @param {number} max - The maximum progress value (100%)
 * @param {function} onProgress - Callback function to update progress
 * @param {Object} [options] - Additional options
 * @param {boolean} [options.sleepFirst=true] - Whether to wait before starting the upload
 * @param {number} [options.minTimeout=400] - Minimum timeout in milliseconds
 * @param {number} [options.maxTimeout=1500] - Maximum timeout in milliseconds
 * @param {number} [options.minIncrement=0.05] - Minimum increment in progress
 * @param {number} [options.maxIncrement=0.1] - Maximum increment in progress
 */
export const simulateUpload = async (max, onProgress, {
    sleepFirst = true,
    minTimeout = 400,
    maxTimeout = 1500,
    minIncrement = 0.05,
    maxIncrement = 0.1,
} = {}) => {
    
    let currentProgress = 0
    
    while (currentProgress < max) {
        
        // Wait between 100-300ms before starting the upload
        // const timeout = Math.floor(Math.random() * 200) + 100
        
        // Wait between 500-2000ms before starting the upload
        const timeout = Math.floor(Math.random() * (maxTimeout - minTimeout)) + minTimeout
        
        // Wait a random time between 100-300ms
        if (sleepFirst)
            await sleep(timeout)
        
        // Calculate a random progress increment between 5-10% of max
        const incrementDiff = maxIncrement - minIncrement
        const increment = Math.floor(Math.random() * incrementDiff * max + minIncrement * max)
        
        // Calculate new progress, but don't exceed max
        const newProgress = Math.min(currentProgress + increment, max)
        
        // Update current progress
        currentProgress = newProgress
        
        // Call the progress callback
        onProgress(currentProgress)
        
        // Wait a random time between 100-300ms
        if (!sleepFirst)
            await sleep(timeout)
        
    }
    
    // Ensure we reach exactly 100%
    if (currentProgress !== max)
        onProgress(max)
    
    return max
    
}

export const filterObject = (obj, fn) => {
    
    return Object.fromEntries(
        Object.entries(obj).filter(([key, value]) => fn(value, key)))
    
}
